import { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { demoTypography } from './theme';

/**
 * Count-up number animation for investor demo stats.
 *
 * @param {number} value - Target number to count up to
 * @param {string} prefix - Text before the number (e.g., "$")
 * @param {string} suffix - Text after the number (e.g., "%", "M", "K")
 * @param {string} label - Description below the number
 * @param {number} duration - Animation duration in ms (default 1500)
 * @param {number} decimals - Decimal places to show (default 0)
 * @param {boolean} animate - Whether to animate (triggers on mount/change)
 */
export default function AnimatedMetric({
    value,
    prefix = '',
    suffix = '',
    label,
    duration = 1500,
    decimals = 0,
    animate = true,
    sx = {},
}) {
    const [displayValue, setDisplayValue] = useState(animate ? 0 : value);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!animate) {
            setDisplayValue(value);
            return;
        }

        setDisplayValue(0);
        startTimeRef.current = null;

        const step = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out cubic for smooth deceleration
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplayValue(eased * value);

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [value, duration, animate]);

    const formatted = decimals > 0
        ? displayValue.toFixed(decimals)
        : Math.round(displayValue).toLocaleString();

    return (
        <Box sx={{ textAlign: 'center', ...sx }}>
            <Typography sx={demoTypography.statNumber}>
                {prefix}{formatted}{suffix}
            </Typography>
            {label && (
                <Typography sx={{ ...demoTypography.statLabel, mt: 0.5 }}>
                    {label}
                </Typography>
            )}
        </Box>
    );
}
